import {
  ConventionRegistrationProductQuery,
  useConventionRegistrationProductQuery,
  useProductAccessQuery,
} from '@/generated/graphql'

type ConventionRegistrationProduct =
  ConventionRegistrationProductQuery['ecommProductBySlug']

/**
 * ecommProductBySlug only returns live products unless `password` is set.
 * If the stub exists but the product is not live, we resolve access and retry with the product id as password (same as product page gated flow).
 */
export function useEcommProductByStubWithUnlock(
  stub: string,
  brandCode: string,
  initialProductData?: ConventionRegistrationProduct
) {
  const queryOptions = {
    enabled: Boolean(stub),
    // Convention registration products are slow-changing and read-only in this flow.
    // Keep fresh enough while avoiding mount/focus network spikes.
    staleTime: 5 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  } as const

  const initial = useConventionRegistrationProductQuery(
    { slug: stub, brandCode },
    {
      ...queryOptions,
      initialData: initialProductData
        ? { ecommProductBySlug: initialProductData }
        : undefined,
    }
  )
  const initialProduct = initial.data?.ecommProductBySlug

  const access = useProductAccessQuery(
    { slug: stub },
    {
      enabled:
        Boolean(stub) && initial.isSuccess && !initialProduct,
      staleTime: 5 * 60 * 1000,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    }
  )

  const accessInfo = access.data?.ecommProductAccessBySlug
  const shouldUnlock =
    accessInfo?.requiresPassword === true && accessInfo.productId != null

  const unlocked = useConventionRegistrationProductQuery(
    {
      slug: stub,
      brandCode,
      password: accessInfo?.productId?.toString(),
    },
    {
      enabled:
        Boolean(stub) &&
        initial.isSuccess &&
        !initialProduct &&
        shouldUnlock,
      staleTime: 5 * 60 * 1000,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    }
  )

  const product = initialProduct ?? unlocked.data?.ecommProductBySlug

  const isPending =
    initial.isPending ||
    (Boolean(stub) && initial.isSuccess && !initialProduct && access.isPending) ||
    (Boolean(stub) && shouldUnlock && unlocked.isPending)

  const isError = initial.isError || unlocked.isError

  return { product, isPending, isError }
}
